import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./App.css";
import "./WelcomePage.css";
import AppFooter from "./AppFooter";
import { clearToken, getToken, handleUnauthorizedResponse } from "./authStorage";

const API_BASE = process.env.REACT_APP_API_BASE || "/api";

function WorkspaceAccountPage() {
  const [contactOpen, setContactOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [outputs, setOutputs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchAccount = async () => {
      try {
        const token = getToken();
        if (!token) throw new Error("Not authenticated");

        const authHeaders = { Authorization: `Bearer ${token}` };
        const [userRes, outputsRes] = await Promise.all([
          fetch(`${API_BASE}/me`, { headers: authHeaders }),
          fetch(`${API_BASE}/account/outputs`, { headers: authHeaders }),
        ]);

        if ([userRes, outputsRes].some(handleUnauthorizedResponse)) return;
        if (!userRes.ok) throw new Error("Failed to load account data");
        if (!outputsRes.ok) throw new Error("Failed to load account outputs");

        const userData = await userRes.json();
        const outputData = await outputsRes.json();
        setUser(userData);
        setOutputs(outputData.outputs || []);
      } catch (err) {
        setError(err.message || "Unable to load account information.");
      } finally {
        setLoading(false);
      }
    };

    fetchAccount();
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const nowSeconds = Math.floor(Date.now() / 1000);
      setOutputs((currentOutputs) =>
        currentOutputs.filter((output) => !output.expires_at || Number(output.expires_at) > nowSeconds)
      );
    }, 60000);

    return () => window.clearInterval(timer);
  }, []);

  const handleLogout = () => {
    clearToken();
    window.location.replace("/login");
  };

  const formatDateTime = (value) => {
    if (!value) return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString([], {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const groupedJobs = outputs.reduce((groups, item) => {
    const key = item.job_id || item.filename;
    if (!groups[key]) {
      groups[key] = {
        job_id: key,
        expires_at_iso: item.expires_at_iso,
        created_at: item.created_at,
        completed_at: item.completed_at,
        items: [],
      };
    }
    groups[key].items.push(item);
    return groups;
  }, {});

  const statementCards = Object.values(groupedJobs).sort((left, right) => {
    const leftTime = new Date(left.completed_at || left.created_at || left.expires_at_iso || 0).getTime();
    const rightTime = new Date(right.completed_at || right.created_at || right.expires_at_iso || 0).getTime();
    return rightTime - leftTime;
  });

  return (
    <div className="app-container welcome-page">
      <main className="welcome-workspace">
        <aside className="welcome-sidebar">
          <div className="welcome-sidebar-brand">
            <Link to="/welcome" className="welcome-sidebar-brand-link">
              <span className="welcome-sidebar-brand-mark" aria-hidden="true" />
              <span className="welcome-sidebar-brand-text">StatementIQ</span>
            </Link>
          </div>

          <div className="welcome-sidebar-nav">
            <div className="welcome-sidebar-group">
              <p className="welcome-sidebar-title">Workspace</p>
              <Link to="/welcome" className="welcome-sidebar-link">
                Convert Statements
              </Link>
            </div>

            <div className="welcome-sidebar-group">
              <p className="welcome-sidebar-title">Help &amp; Support</p>
              <Link to="/account" className="welcome-sidebar-link active">
                Account
              </Link>
              <Link to="/workspace-faq" className="welcome-sidebar-link">
                FAQ
              </Link>
            </div>

            <div className="welcome-sidebar-group">
              <p className="welcome-sidebar-title">Legal</p>
              <Link to="/privacy" className="welcome-sidebar-link">
                Privacy Policy
              </Link>
              <Link to="/terms" className="welcome-sidebar-link">
                Terms of Service
              </Link>
            </div>
          </div>

          <div className="welcome-sidebar-support-card">
            <strong>Need help?</strong>
            <p>Our support team is here to assist you.</p>
            <button
              type="button"
              className="welcome-sidebar-support-button"
              onClick={() => setContactOpen(true)}
            >
              Contact Support
            </button>
          </div>
          <button
            type="button"
            className="welcome-sidebar-link welcome-sidebar-button welcome-sidebar-logout"
            onClick={handleLogout}
          >
            Logout
          </button>
        </aside>

        <section className="welcome-content">
          <div className="welcome-body-grid">
            <div className="welcome-main-panel workspace-scroll-panel">
              <div className="welcome-heading-row">
                <div>
                  <h1>{user?.name || "Account"}</h1>
                </div>
              </div>

              {loading && <p className="workspace-state-text">Loading account details...</p>}
              {error && <p className="workspace-state-text">{error}</p>}

              {user && (
                <div className="workspace-account-grid workspace-account-single">
                  <section className="workspace-account-card workspace-account-wide">
                    <h2>Completed Statements</h2>
                    <p className="workspace-account-summary">
                      Download converted Excel files ready for use.
                    </p>
                    {statementCards.length === 0 ? (
                      <p>No completed statements are currently available.</p>
                    ) : (
                      <div className="workspace-account-cards">
                        {statementCards.map((jobGroup) => {
                          const outputItem = jobGroup.items.find((item) => item.type === "output");

                          if (!outputItem) {
                            return null;
                          }

                          return (
                            <article className="workspace-statement-card" key={jobGroup.job_id}>
                              <div className="workspace-account-table-wrap">
                                <table className="workspace-account-table">
                                  <thead>
                                    <tr>
                                      <th>Excel File Name</th>
                                      <th>Available Until</th>
                                      <th>Download</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td className="workspace-account-table-file" data-label="Excel File Name">
                                        {outputItem.filename}
                                      </td>
                                      <td className="workspace-account-table-date" data-label="Available Until">
                                        {formatDateTime(jobGroup.expires_at_iso)}
                                      </td>
                                      <td className="workspace-account-table-action" data-label="Download">
                                        <a className="welcome-inline-link-button" href={outputItem.download_url}>
                                          Download Excel
                                        </a>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    )}
                  </section>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      {contactOpen && (
        <div className="welcome-tips-popup-overlay" onClick={() => setContactOpen(false)}>
          <div className="welcome-tips-popup" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="welcome-tips-close"
              aria-label="Close contact popup"
              onClick={() => setContactOpen(false)}
            >
              ×
            </button>

            <div className="welcome-tips-header">
              <h2>Contact Us</h2>
            </div>

            <div className="welcome-contact-body">
              <p>
                If you need help with bank statement uploads, conversion issues, or account access,
                please contact our support team.
              </p>
              <p>
                Include your name, company, and a short summary of the issue so we can help quickly.
              </p>
              <p>
                <a href="mailto:support@statementiq.co.uk">support@statementiq.co.uk</a>
              </p>
            </div>
          </div>
        </div>
      )}

      <AppFooter />
    </div>
  );
}

export default WorkspaceAccountPage;
